import { apiRequest } from './api'
import { displayBrand } from './brands'

const cloud = process.env.REACT_APP_CLOUDINARY_CLOUD || 'deymt9uyh'
const clean = value => String(value || '').trim()
const number = value => Number(value || 0)
const normalize = value => clean(value).toLowerCase().replace(/[^a-z0-9]+/g, '')
const imageValue = value => typeof value === 'string' ? value : clean(value?.image_url || value?.imageUrl || value?.url)
const unique = values => [...new Set(values.map(clean).filter(Boolean))]
const explicitImages = row => unique([
  row?.shared_image_url,
  row?.front_image_url,
  row?.back_image_url,
  row?.main_image_url,
  row?.variant_image_url,
  row?.ean_image_url,
  row?.image_url,
  ...(Array.isArray(row?.images) ? row.images.map(imageValue) : [])
])
const designCode = row => clean(row?.design_code || row?.pattern_code || row?.style_code || row?.mark_code || row?.model_code)
export const productGroupKey = product => product.style_key || [normalize(product.rawBrand || product.brand_name || product.brand), normalize(product.name || product.product_name), normalize(product.gender), String(product.categoryId || product.category_id || product.category || ''), normalize(designCode(product)), normalize(product.fit || product.fit_type), String(product.pack_size || 1)].join('|')

export function normalizeProduct(row, index = 0) {
  const variants = Array.isArray(row?.variants) && row.variants.length ? row.variants : [row]
  const primary = variants.find(item => item?.is_active !== false) || variants[0] || row
  const ean = clean(primary?.ean_code || primary?.barcode || row?.ean_code || row?.barcode)
  const images = unique([...explicitImages(row), ...explicitImages(primary)])
  const originalB2C = number(primary?.original_price_b2c || row?.original_price_b2c || primary?.mrp || row?.mrp)
  const finalB2C = number(primary?.final_price_b2c ?? row?.final_price_b2c ?? primary?.sale_price ?? row?.sale_price ?? originalB2C)
  const originalB2B = number(primary?.original_price_b2b || row?.original_price_b2b || primary?.mrp || row?.mrp)
  const finalB2B = number(primary?.final_price_b2b ?? row?.final_price_b2b ?? primary?.sale_price ?? row?.sale_price ?? originalB2B)
  const rawBrand = clean(row?.brand_name || row?.brand)
  const brand = displayBrand(rawBrand)
  const name = clean(row?.product_name || row?.name || row?.title || 'Product')
  const design = clean(row?.design_code || row?.pattern_code || row?.style_code || row?.mark_code)
  const fit = clean(row?.fit_type || row?.fit)
  const designKey = [normalize(brand), normalize(name), normalize(design), normalize(fit)].filter(Boolean).join('|')
  return {
    ...row,
    id: row?.product_id || row?.id || index + 1,
    productId: row?.product_id || row?.id,
    variantId: primary?.variant_id || primary?.id || row?.variant_id,
    name,
    brand,
    rawBrand,
    gender: clean(row?.gender || row?.category_root).toUpperCase(),
    categoryId: number(row?.category_id),
    category: clean(row?.category_name || row?.category || row?.category_slug),
    categorySlug: clean(row?.category_slug),
    categoryPath: clean(row?.category_path),
    categoryRoot: clean(row?.category_root).toUpperCase(),
    designKey: designKey || `${normalize(brand)}|${normalize(name)}`,
    ean,
    databaseImages: images,
    images: images.length ? images : [ean ? `https://res.cloudinary.com/${cloud}/image/upload/f_auto,q_auto/products/${encodeURIComponent(ean)}` : ''],
    variants,
    colours: unique([...(row?.colours || []), ...variants.flatMap(variant => [variant?.colour, variant?.color])]),
    sizes: unique([...(row?.sizes || []), ...variants.map(variant => variant?.size)]),
    originalB2C,
    finalB2C,
    originalB2B,
    finalB2B,
    available: number(row?.style_available ?? primary?.available_qty ?? primary?.on_hand ?? row?.available_qty ?? row?.on_hand)
  }
}

export function groupProducts(rows = []) {
  const groups = new Map()
  rows.map(normalizeProduct).forEach(product => {
    const key = productGroupKey(product)
    if (!groups.has(key)) groups.set(key, { ...product, designKey: key, variants: [], images: [], colours: [], sizes: [], available: 0, productIds: [] })
    const group = groups.get(key)
    group.productIds = unique([...group.productIds, product.productId])
    const variantMap = new Map(group.variants.map(variant => [clean(variant?.variant_id || variant?.id || variant?.ean_code || `${variant?.size}|${variant?.colour || variant?.color}`), variant]))
    product.variants.forEach(variant => variantMap.set(clean(variant?.variant_id || variant?.id || variant?.ean_code || `${variant?.size}|${variant?.colour || variant?.color}`), variant))
    group.variants = [...variantMap.values()]
    group.images = unique([...group.images, ...product.images])
    group.colours = unique([...group.colours, ...product.colours])
    group.sizes = unique([...group.sizes, ...product.sizes])
    group.available += product.available
  })
  return [...groups.values()]
}

export async function fetchProducts(params = {}) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value))
  })
  const data = await apiRequest(`/api/products/catalogue?${query.toString()}`)
  return groupProducts(Array.isArray(data) ? data : data?.products || [])
}

export const getPrice = (product, type = 'B2C') => String(type).toUpperCase() === 'B2B'
  ? { original: product.originalB2B, final: product.finalB2B }
  : { original: product.originalB2C, final: product.finalB2C }

export const formatPrice = value => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

export async function fetchProductPage(params = {}, options = {}) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key,value]) => { if (value !== undefined && value !== null && value !== '') query.set(key,String(value)) })
  const data = await apiRequest(`/api/products/catalogue?${query}`, options)
  return {...data,products:groupProducts(data.products || [])}
}
export const fetchFacets = (params = {}, options = {}) => {
  const query = new URLSearchParams(Object.entries(params).filter(([,v])=>v !== undefined && v !== null && v !== ''))
  return apiRequest(`/api/products/facets?${query}`, options)
}
